/**
 * Sale pricing for mock products.
 *
 * Products carry a single `price` and nothing else, so — like `productGameType`
 * and `salesInWindow` — the discount is derived deterministically from the
 * product id rather than persisted. `price` stays the authoritative amount the
 * buyer pays; the derived `compareAtPrice` is the struck-through original.
 *
 * Free products are never "on sale": a strikethrough on 0 is meaningless, and it
 * would make the On Sale and Free Products views overlap for no reason.
 */

/** Roughly this share of paid products carry a discount. */
const SALE_SHARE = 0.15;
const DISCOUNT_STEPS = [10, 15, 20, 25, 30, 40, 50];
/** Original prices are round numbers, so snap to the nearest 50 cents. */
const PRICE_STEP = 50;

/** Deterministic string hash (djb2) — stable across runs and platforms. */
function hash(input: string): number {
  let value = 5381;
  for (let index = 0; index < input.length; index++) {
    value = (value * 33) ^ input.charCodeAt(index);
  }
  return value >>> 0;
}

/**
 * The pre-discount price in cents, or `undefined` when the product is not on
 * sale. Always strictly greater than `price`, so a caller can divide safely.
 */
export function productCompareAtPrice(productId: string, price: number): number | undefined {
  if (price <= 0) return undefined;
  if (hash(`sale:${productId}`) % 1000 >= SALE_SHARE * 1000) return undefined;

  const percentOff = DISCOUNT_STEPS[hash(`sale-depth:${productId}`) % DISCOUNT_STEPS.length]!;
  const original = Math.round(price / (1 - percentOff / 100) / PRICE_STEP) * PRICE_STEP;
  // Rounding can land on or below `price` for cheap products; keep it a real discount.
  return Math.max(original, price + PRICE_STEP);
}

export function isOnSale(productId: string, price: number): boolean {
  return productCompareAtPrice(productId, price) !== undefined;
}

/**
 * The badge percentage, computed from the two prices actually shown rather than
 * from the step that generated them — so "-30%" never contradicts the numbers
 * beside it after rounding.
 */
export function discountPercent(price: number, compareAtPrice: number): number {
  return Math.round((1 - price / compareAtPrice) * 100);
}
